import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { TENANT_STAFF_ACCESS_TOKEN_KEY } from '@/constants';
import {
  deleteCookie,
  getCookie,
  setCookie,
  tenantStaffLogin,
  loadTenantStaffProfile,
  ITenantStaff,
} from '@/services';

type TenantStaffUser = {
  id: string;
  email: string;
  name: string;
  role: string;
};

const toStaffUser = (staff: ITenantStaff): TenantStaffUser => ({
  id: staff.id,
  email: staff.email,
  name:
    [staff.firstName, staff.lastName].filter(Boolean).join(' ') ||
    staff.email.split('@')[0] ||
    'Tenant Staff',
  role: staff.role,
});

interface TenantStaffAuthContextState {
  staff: TenantStaffUser | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  login(
    email: string,
    password: string
  ): Promise<{ success: boolean; message?: string }>;
  logout(): void;
}

const TenantStaffAuthContext = createContext<
  TenantStaffAuthContextState | undefined
>(undefined);

export const useTenantStaffAuth = (): TenantStaffAuthContextState => {
  const ctx = useContext(TenantStaffAuthContext);
  if (!ctx) {
    throw new Error(
      'useTenantStaffAuth must be used within TenantStaffAuthProvider'
    );
  }
  return ctx;
};

export const TenantStaffAuthProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [staff, setStaff] = useState<TenantStaffUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const isAuthenticated = Boolean(accessToken);

  const persistAccessToken = (token: string | null) => {
    setAccessToken(token);
    token
      ? setCookie(TENANT_STAFF_ACCESS_TOKEN_KEY, token, 7)
      : deleteCookie(TENANT_STAFF_ACCESS_TOKEN_KEY);
  };

  const persistStaff = (staffData: TenantStaffUser | null) => {
    if (staffData) {
      setCookie('tenantStaff', JSON.stringify(staffData), 7);
      setStaff(staffData);
      return;
    }

    deleteCookie('tenantStaff');
    setStaff(null);
  };

  // The API's tenant middleware resolves the tenant for authenticated requests
  // from the `x-tenant-id` header (sourced from the `tenantId` cookie by the API
  // client). Tenant-staff sessions must set it so their requests aren't rejected
  // with "Authenticated API requests must include x-tenant-id header".
  const persistTenantId = (id?: string | null) => {
    if (id) setCookie('tenantId', id, 7);
  };

  useEffect(() => {
    const bootstrap = async () => {
      const storedAccessToken = getCookie(TENANT_STAFF_ACCESS_TOKEN_KEY);
      const storedStaff = getCookie('tenantStaff');

      if (!storedAccessToken) {
        setLoading(false);
        return;
      }

      persistAccessToken(storedAccessToken);

      if (storedStaff) {
        try {
          setStaff(JSON.parse(storedStaff));
        } catch {
          deleteCookie('tenantStaff');
        }
      }

      // Refresh profile from the API so cached data stays in sync.
      try {
        const response = await loadTenantStaffProfile(storedAccessToken);
        if (response?.success && response.data?.profile) {
          persistStaff(toStaffUser(response.data.profile));
          persistTenantId(response.data.profile.tenantId);
        }
      } catch {
        // Token likely invalid/expired — clear the session.
        persistAccessToken(null);
        persistStaff(null);
      }

      setLoading(false);
    };

    bootstrap();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    if (!email || !password) {
      return {
        success: false,
        message: 'Email and password are required',
      };
    }

    const response = await tenantStaffLogin(email, password);

    if (!response?.success || !response.data) {
      return {
        success: false,
        message: response?.message || 'Login failed',
      };
    }

    // Set the tenant cookie BEFORE any authenticated request fires so the API
    // client can attach the x-tenant-id header the tenant middleware requires.
    persistTenantId(response.data.staff.tenantId);
    persistAccessToken(response.data.token);
    persistStaff(toStaffUser(response.data.staff));

    return { success: true };
  }, []);

  const logout = useCallback(() => {
    persistAccessToken(null);
    persistStaff(null);
  }, []);

  const value = useMemo<TenantStaffAuthContextState>(
    () => ({
      staff,
      accessToken,
      isAuthenticated,
      loading,
      login,
      logout,
    }),
    [staff, accessToken, isAuthenticated, loading, login, logout]
  );

  return (
    <TenantStaffAuthContext.Provider value={value}>
      {children}
    </TenantStaffAuthContext.Provider>
  );
};
